import { memo, useEffect, useRef, useState } from 'react'
import cn from 'classnames'

import { copy, LunyaMascot } from '~/modules/lunya'
import { ANALYTICS_EVENTS, useAnalytics } from '~/shared/lib/analytics'
import { Button, Screen, Spacer, Text, VStack } from '~/shared/ui'

import { AcceptTermsMutation, UpdateUserMutation } from '../../api'
import { GenderChips, NameInput, TermsAcceptNote } from '../../components'
import { useGenderGuess } from '../../hooks'
import { MASCOT_SIZE } from './config'
import type { NameStepWidgetProps } from './types'

import styles from './NameStepWidget.module.scss'

/**
 * A1 · Знакомство. Единственный шаг слайса, где имя/пол уходят на бэк (спека 02 §A1).
 * Гендер определяется молча по словарю; A1·2 показывается только при неуверенном угадывании.
 */
export const NameStepWidget = memo(function NameStepWidget(props: NameStepWidgetProps) {
  const { initialName, initialGender, onNext } = props
  const { track } = useAnalytics()

  const [name, setName] = useState(initialName)
  const [isEditing, setIsEditing] = useState(false)
  const [manualGender, setManualGender] = useState<typeof initialGender>(initialGender)

  const guessedGender = useGenderGuess(name)
  const isAmbiguous = name.trim() !== '' && guessedGender === null
  const effectiveGender = guessedGender ?? manualGender

  const hasTrackedEdit = useRef(false)
  const hasTrackedGenderAsked = useRef(false)

  useEffect(() => {
    if (!hasTrackedEdit.current && name !== initialName) {
      hasTrackedEdit.current = true
      track(ANALYTICS_EVENTS.onbNameEdited())
    }
  }, [name, initialName, track])

  useEffect(() => {
    if (!hasTrackedGenderAsked.current && isAmbiguous) {
      hasTrackedGenderAsked.current = true
      track(ANALYTICS_EVENTS.onbGenderAsked())
    }
  }, [isAmbiguous, track])

  const [updateUser, { isPending }] = UpdateUserMutation.useMutation()
  const [acceptTerms] = AcceptTermsMutation.useMutation()

  const trimmedName = name.trim()
  const canSubmit = trimmedName !== '' && (!isAmbiguous || effectiveGender !== null)
  const hasError = trimmedName === ''

  const handleConfirmEditing = () => {
    ;(document.activeElement as HTMLElement | null)?.blur()
    setIsEditing(false)
  }

  const handleSubmit = async () => {
    if (!canSubmit) {
      return
    }

    // Акцепт оферты — это и есть нажатие этой кнопки: под ней стоит строка, которая так и
    // говорит (`TermsAcceptNote`, ст. 438 ГК РФ). Записываем его тем же действием, а не
    // «когда-нибудь потом»: договор считается заключённым здесь, и подтверждение обязано
    // относиться к этому моменту.
    //
    // Шаг НЕ ждёт ответа и НЕ падает, если он не придёт. Регистрация — не место, где человека
    // останавливает сетевой сбой в служебной записи; непринятые условия сами вернутся шторкой
    // при следующем входе (`TermsAcceptSheet`), потому что `terms_version` останется пустым.
    track(ANALYTICS_EVENTS.termsAccepted({ source: 'onboarding' }))
    void acceptTerms({ source: 'onboarding' })

    await updateUser({ firstName: trimmedName, gender: effectiveGender ?? undefined })
    onNext({ name: trimmedName, gender: effectiveGender })
  }

  return (
    <Screen background='FLOW' hasStickyCta>
      <Screen.Content>
        <div className={styles.root}>
          <div className={styles.mascot}>
            <LunyaMascot pose='CALLING' size={MASCOT_SIZE} />
          </div>
          <VStack gap='SMALL_M' align='CENTER'>
            <Text variant='DISPLAY' align='CENTER'>
              {copy.onb.a1.title}
            </Text>
            <Text variant='BODY_S' color='SECONDARY' align='CENTER'>
              {copy.onb.a1.subtitle({ gender: effectiveGender })}
            </Text>
          </VStack>
          <Spacer grow />
          <VStack gap='SMALL_M'>
            <NameInput
              value={name}
              onChange={setName}
              onFocus={() => setIsEditing(true)}
              onBlur={() => setIsEditing(false)}
              hasError={hasError}
              autoFocus={initialName === ''}
            />

            {/* Место под вопрос о поле зарезервировано всегда: иначе появление чипов
                толкает поле имени вверх, и содержимое экрана прыгает при вводе. */}
            <div
              className={cn(styles.genderSlot, isAmbiguous && styles.genderSlotVisible)}
              aria-hidden={!isAmbiguous}
            >
              <GenderChips value={manualGender} onChange={setManualGender} />
            </div>
          </VStack>
        </div>
      </Screen.Content>
      {/* Текст кнопки не меняется при вводе: подмена подписи главного действия на лету —
          плохой паттерн, пользовательница теряет из виду, что кнопка вообще делает.
          Отклонение от спеки 02 §A1 («Так меня и зовут») — по прямому решению основателя. */}
      <Screen.Footer>
        <VStack gap='SMALL_M'>
          <Button
            variant='PRIMARY'
            width='FULL'
            text={copy.onb.a1.cta}
            isDisabled={!canSubmit}
            isLoading={isPending}
            onPress={handleSubmit}
          />
          {/* Под кнопкой, а не над ней (решение основателя): следующим шагом человек называет
              дату рождения, и место, где написано, что с ней происходит, обязано быть видно
              до этого.

              Раньше здесь стояла одна ссылка «Что я делаю с твоими данными». Теперь — строка
              акцепта: нажатие кнопки и есть заключение договора, и сказать об этом надо ДО
              нажатия, иначе акцепта не получилось (ст. 438 ГК РФ). Прежняя формулировка не
              потерялась — она стала второй половиной этой же строки. */}
          <TermsAcceptNote />
        </VStack>
      </Screen.Footer>
    </Screen>
  )
})
